import { useCallback, useEffect, useMemo } from "react"
import { get, isNil } from "lodash"
import { useTheme, Box } from "@mui/material"
import {
  Feature,
  useHighLightFeaturesMap,
} from "hooks/feature/useHighLightFeaturesMap"

import { GTM_AMENITY_SEARCH } from "constant"
import { pushDataLayer } from "utils/googletagmanager"

import { useSearchParams } from "react-router-dom"
import { useTranslation } from "providers/i18n"
import { useVenue } from "providers/venue"
import { SearchToggleButton } from "components/domains/search/SearchToggleButton"
import { useAppUI } from "providers/ui"
import { useMap } from "hooks/map/useMap"
import { useFeatureCategory } from "hooks/feature"
import { useGeoLocation } from "providers/geolocation"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { useGetMapViewFromUrl } from "hooks/map"

import { PageLayout } from "layouts"
import { FeatureChipToggleBar } from "components/domains/features/ChipToggleBar"
import { ScrollableTab } from "components/ScrollableTab"
import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { VenueSelector } from "components/VenueSelector"
import { OrdinalSelector } from "components/OrdinalSelector"
import { OrdinalSelectMenu } from "components/OrdinalSelector/OrdinalSelectMenu"

export const FeatureByCategory = ({
  Layout = PageLayout,
  AppNavigation = DefaultAppNavigation,
}) => {
  const theme = useTheme()
  const { locationVenue, locationOrdinal } = useGeoLocation()
  const locationVenueId = get(locationVenue, "id")
  const [searchParams, setSearchParams] = useSearchParams()
  const { venues, isMultiOrdinalVenue, dataLoaded } = useVenue()
  const { openSearch } = useAppUI()
  const { t } = useTranslation()
  const { viewingVenueLevels, isMultiVenueProject, onChangeVenue } = useMap()
  const {
    pageLayout,
    ordinalSelector,
    quickLinkContainer, //TODO:: planning about quick search's working flow later
    searchBarContainer,
    toolbarContainer,
    ordinalSelectMenu,
    venueSelector,
  } = theme.components[`AppMap`]?.styleOverrides || {}

  const {
    viewingCategory,
    viewingVenueId,
    viewingOrdinal,
    comingFromOrdinal,
    featureCategories,
    searchableFeatures,
    handleClickQuickSearch,
  } = useFeatureCategory({
    initialVenueId: locationVenueId,
    initialOrdinal: locationOrdinal,
  })

  const enableMap =
    !isNil(viewingOrdinal) && isNil(comingFromOrdinal) && dataLoaded

  const { mapView } = useGetMapViewFromUrl()

  const { flyTo } = useIndoorMap()

  useEffect(() => {
    flyTo(mapView)
  }, [flyTo, mapView])

  const onClickOrdinal = useCallback(
    (ordinal) => {
      const currentParams = Object.fromEntries(searchParams.entries())
      setSearchParams({ ...currentParams, ordinal })
    },
    [setSearchParams, searchParams]
  )

  const parsedOrdinal = useMemo(() => Number(viewingOrdinal), [viewingOrdinal])

  useHighLightFeaturesMap(searchableFeatures as Feature[], parsedOrdinal, {
    enabled: enableMap,
  })

  useEffect(() => {
    if (viewingCategory) {
      pushDataLayer({
        event: GTM_AMENITY_SEARCH,
        amenity_category: viewingCategory,
      })
    }
  }, [viewingCategory])

  return (
    <>
      <Layout sx={pageLayout}>
        <Box sx={toolbarContainer}>
          <Box sx={searchBarContainer}>
            <SearchToggleButton
              placeholder={t("Search for your destination")}
              onClick={openSearch}
            />
          </Box>
          <Box sx={quickLinkContainer}>
            <ScrollableTab>
              <FeatureChipToggleBar
                categories={featureCategories}
                onClick={handleClickQuickSearch}
              />
            </ScrollableTab>
          </Box>
        </Box>
        {isMultiOrdinalVenue && (
          <Box sx={ordinalSelector}>
            <OrdinalSelector
              selectedOrdinal={Number(viewingOrdinal)}
              levels={viewingVenueLevels}
              onClickOrdinal={onClickOrdinal}
            />
          </Box>
        )}
        {isMultiOrdinalVenue && (
          <Box sx={ordinalSelectMenu}>
            <OrdinalSelectMenu
              selectedOrdinal={Number(viewingOrdinal)}
              levels={viewingVenueLevels}
              onChangeOrdinal={onClickOrdinal}
            />
          </Box>
        )}

        {isMultiVenueProject && (
          <Box sx={venueSelector}>
            <VenueSelector
              selectedVenue={viewingVenueId}
              venues={venues}
              onClickVenue={onChangeVenue}
              currentVenue={locationVenueId}
            />
          </Box>
        )}
      </Layout>
    </>
  )
}
